import type { Artboard, File, RiveCanvas, StateMachineInstance, WrappedRenderer } from "@rive-app/webgl2-advanced";
import type { Log, PresentationTicker } from "@slot/presentation";
import { fetchBytes } from "./RiveArtboard";

/** A drawn frame is due a little early rather than a display frame late (two 60 Hz frames sum just under 1/30 s). */
const FRAME_TOLERANCE_SECONDS = 0.002;

/** What a canvas layer draws and how often. */
export interface RiveCanvasLayerSpec {
  /** The .riv file's URL. */
  url: string;
  /** The state machine every artboard plays. */
  stateMachine: string;
  /** Cover-fit inflation (1.002: no edge shows). */
  overscan: number;
  /** One drawn frame per this long, in engine seconds; the state machine advances by the time since the last. */
  frameSeconds: number;
}

/** The runtime's objects once the file has loaded: the canvas's own renderer draws into it. */
interface Loaded {
  rive: RiveCanvas;
  file: File;
  renderer: WrappedRenderer;
}

/** One of the file's artboards and the state machine playing it. */
interface Scene {
  artboard: Artboard;
  machine: StateMachineInstance;
}

/**
 * A Rive file drawn straight to the screen on a canvas of its own — the background layer under a transparent game canvas
 * (decision 67): a full-screen animation costs its render alone, nothing is copied into the game's canvas. It shows one
 * artboard at a time (`show`; each is played from the moment it is first shown), covering the canvas centred, on engine
 * time (the presentation ticker, so the dev tools' pause and step hold it too) at the spec's frame rate. The canvas stays
 * hidden until its first drawn frame, so what lies under it (a still) shows until then, and for good if the file fails.
 */
export class RiveCanvasLayer {
  public readonly canvas: HTMLCanvasElement;
  private loaded: Loaded | null = null;
  private readonly scenes = new Map<string, Scene>();
  /** The artboard asked for, shown once the file has loaded. */
  private wanted: string | null = null;
  /** Engine seconds since the last drawn frame. */
  private pendingSeconds = 0;
  private stopTicking: (() => void) | null = null;
  private disposed = false;

  public constructor(
    private readonly spec: RiveCanvasLayerSpec,
    private readonly deps: { root: HTMLElement; runtime: () => Promise<RiveCanvas>; ticker: PresentationTicker; log: Log },
  ) {
    this.canvas = document.createElement("canvas");
    this.canvas.style.cssText = "position: absolute; inset: 0; width: 100%; height: 100%; visibility: hidden";
    deps.root.appendChild(this.canvas);
  }

  /** Loads the runtime and the file, then draws; false, logged, when either fails (the canvas stays hidden). */
  public async load(): Promise<boolean> {
    try {
      const [rive, bytes] = await Promise.all([this.deps.runtime(), fetchBytes(this.spec.url)]);
      const file = await rive.load(bytes, undefined, false);
      if (this.disposed) {
        file.unref();
        return false;
      }
      this.loaded = { rive, file, renderer: rive.makeRenderer(this.canvas, false) };
      this.pendingSeconds = this.spec.frameSeconds;
      this.stopTicking = this.deps.ticker.add((deltaSeconds) => this.tick(deltaSeconds));
      return true;
    } catch (error) {
      this.deps.log(`[RiveCanvasLayer] "${this.spec.url}" not loaded: ${error instanceof Error ? error.message : String(error)}`);
      return false;
    }
  }

  /** Shows the named artboard from the next frame on (before the file has loaded: once it has). */
  public show(artboard: string): void {
    if (artboard === this.wanted) return;
    this.wanted = artboard;
    this.pendingSeconds = this.spec.frameSeconds;
  }

  /** The drawing buffer at `width` × `height` CSS pixels times `pixelRatio`, redrawn on the next frame (a resize clears it). */
  public resize(width: number, height: number, pixelRatio: number): void {
    const bufferWidth = Math.max(1, Math.round(width * pixelRatio));
    const bufferHeight = Math.max(1, Math.round(height * pixelRatio));
    if (bufferWidth === this.canvas.width && bufferHeight === this.canvas.height) return;
    this.canvas.width = bufferWidth;
    this.canvas.height = bufferHeight;
    this.pendingSeconds = this.spec.frameSeconds;
  }

  public dispose(): void {
    this.disposed = true;
    this.stopTicking?.();
    for (const { machine, artboard } of this.scenes.values()) {
      machine.delete();
      artboard.delete();
    }
    this.scenes.clear();
    if (this.loaded) {
      this.loaded.renderer.delete();
      this.loaded.file.unref();
      this.loaded = null;
    }
    this.canvas.remove();
  }

  private tick(deltaSeconds: number): void {
    this.pendingSeconds += deltaSeconds;
    const loaded = this.loaded;
    if (!loaded || this.pendingSeconds < this.spec.frameSeconds - FRAME_TOLERANCE_SECONDS) return;
    const scene = this.scene(loaded);
    if (!scene) return;
    this.draw(loaded, scene, this.pendingSeconds);
    this.pendingSeconds = 0;
  }

  /** The wanted artboard's scene, made the first time it is shown; null before a name, or for a name the file lacks (logged once). */
  private scene({ rive, file }: Loaded): Scene | null {
    const name = this.wanted;
    if (!name) return null;
    const known = this.scenes.get(name);
    if (known) return known;
    const artboard = file.artboardByName(name);
    const stateMachine = artboard ? artboard.stateMachineByName(this.spec.stateMachine) : null;
    if (!artboard || !stateMachine) {
      this.deps.log(`[RiveCanvasLayer] "${this.spec.url}" has no artboard "${name}" with a state machine "${this.spec.stateMachine}"`);
      artboard?.delete();
      this.wanted = null;
      return null;
    }
    const scene = { artboard, machine: new rive.StateMachineInstance(stateMachine, artboard) };
    this.scenes.set(name, scene);
    return scene;
  }

  private draw({ rive, renderer }: Loaded, scene: Scene, seconds: number): void {
    scene.machine.advanceAndApply(seconds);
    const { width, height } = this.canvas;
    const over = (this.spec.overscan - 1) / 2;
    renderer.clear();
    renderer.save();
    renderer.align(rive.Fit.cover, rive.Alignment.center, { minX: -width * over, minY: -height * over, maxX: width * (1 + over), maxY: height * (1 + over) }, scene.artboard.bounds);
    scene.artboard.draw(renderer);
    renderer.restore();
    renderer.flush();
    rive.resolveAnimationFrame();
    this.canvas.style.visibility = "visible";
  }
}
